import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/feature/Reveal';

const PRESETS = [
  {
    id: 'conservative',
    name: 'Conservative',
    balance: '$500 – $1,000',
    risk: '0.5%',
    lots: '0.01',
    note: 'Safest starter sizing for small accounts.',
    featured: false,
  },
  {
    id: 'balanced',
    name: 'Balanced',
    balance: '$1,000 – $5,000',
    risk: '1.0%',
    lots: '0.02 – 0.05',
    note: 'The recommended default for most users.',
    featured: true,
  },
  {
    id: 'growth',
    name: 'Growth',
    balance: '$5,000 – $20,000',
    risk: '1.5%',
    lots: '0.05 – 0.20',
    note: 'Higher compounding with higher swings.',
    featured: false,
  },
  {
    id: 'pro',
    name: 'Professional',
    balance: '$20,000+',
    risk: '1.0%',
    lots: '0.20+',
    note: 'Scaled sizing with lower relative risk.',
    featured: false,
  },
];

export default function RiskPresets() {
  return (
    <section id="presets" className="py-14 md:py-20 bg-background-100 border-y border-background-200 scroll-mt-24">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="Risk presets"
              title="Pick the preset that matches your balance"
              subtitle="Risk is always a fixed fraction of equity — never a fixed lot size. Tell us your balance in the chat and we send the right preset."
            />
          </Reveal>

          <Reveal delay={100}>
            <div className="mt-10 rounded-lg border border-background-200 overflow-hidden bg-background-50">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <caption className="sr-only">
                    GoldWarden EA risk presets by account balance, showing risk per trade and typical lot sizes
                  </caption>
                  <thead>
                    <tr className="bg-background-100 text-left text-xs uppercase tracking-wide text-foreground-500">
                      <th className="px-5 py-4 font-semibold">Preset</th>
                      <th className="px-5 py-4 font-semibold">Account Balance</th>
                      <th className="px-5 py-4 font-semibold">Risk / Trade</th>
                      <th className="px-5 py-4 font-semibold">Typical Lots</th>
                      <th className="px-5 py-4 font-semibold">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200">
                    {PRESETS.map((preset) => (
                      <tr
                        key={preset.id}
                        className={preset.featured ? 'bg-accent-50' : 'bg-background-50 hover:bg-background-100 transition-colors'}
                      >
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center gap-2">
                            <span className="font-heading text-sm font-semibold text-foreground-950">
                              {preset.name}
                            </span>
                            {preset.featured ? (
                              <span className="rounded-full bg-accent-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-950">
                                Popular
                              </span>
                            ) : null}
                          </span>
                        </td>
                        <td className="px-5 py-4 font-num text-foreground-700 whitespace-nowrap">{preset.balance}</td>
                        <td className="px-5 py-4 font-num font-semibold text-primary-600 whitespace-nowrap">
                          {preset.risk}
                        </td>
                        <td className="px-5 py-4 font-num text-foreground-700 whitespace-nowrap">{preset.lots}</td>
                        <td className="px-5 py-4 text-xs md:text-sm text-foreground-600">{preset.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Reveal>

          <p className="mt-3 flex items-center gap-1.5 text-xs text-foreground-500 lg:hidden">
            <i className="ri-drag-move-2-line" aria-hidden="true"></i>
            On a phone? Swipe the table sideways to compare every preset
          </p>

          <p className="mt-3 text-xs text-foreground-500">
            Lot values are indicative for XAUUSD at a typical broker contract size. The EA recalculates
            sizing live based on your stop distance and equity.
          </p>
        </div>
      </div>
    </section>
  );
}